import { useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import FlexibleTable, {
    SortFilterValue,
    TableFilter,
} from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import TablePaginatorWrapper from "src/shared/components/data-display/FlexibleTable/sections/TablePaginatorWrapper/TablePaginatorWrapper"
import TableWrappper from "src/pages/reservaciones/inicio/sections/TableWrapper/TableWrapper"
import {
    FlexibleTableHeaderColumn,
    FlexibleTableRow,
} from "src/shared/components/data-display/FlexibleTable/flexible-table-items.interface"
import FloatButon from "src/shared/components/layout/FloatButon/FloatButon"
import { Stringify } from "src/shared/types/Stringify"
import ReportWrapper from "../../components/report-wrapper/ReportWrapper"
import CardReports from "../../components/card-reports/CardReports"
import CardReport, { CardReportBars, CardReportDouble, CardReportList } from "../../components/card-report/CardReport"
import reportSelectedHandler from "../../helpers/reportSelectedHandler"
import { ReportsOptions } from "../../Reports.type"
import {
    GeneroHuesped,
    RegistroHuespedes as RegistroHuespedesType,
    TablaRegistroHuesped,
    TipoDocumentoHuesped,
    TipoHuesped,
} from "./RegistroHuespedes.type"
import { getRegistroHuespedesDashboard, registroHuespedesDummy } from "./RegistroHuespedes.dummy"
import RegistroHuespedesDetail from "./detail/RegistroHuespedesDetail"
import useRegistroHuespedesDetail from "./detail/hooks/useRegistroHuespedesDetail"
import "../../Reports.css"
import "./RegistroHuespedes.css"

type HeaderKey = Stringify<TablaRegistroHuesped>

const PAGE_SIZE = 15
const EMPTY_VALUE = "-"

const enumToFilterMenu = (values: string[]) => values.map((value) => ({ value, valueToDisplay: value }))

const toSortableDate = (date: string | null, time?: string) =>
    date ? DateTime.fromFormat(`${date} ${time || "00:00"}`, "dd/MM/yyyy HH:mm").toMillis() : 0

// Valor usado para ordenar cada columna ordenable
const sortGetters: Partial<Record<HeaderKey, (r: TablaRegistroHuesped) => number>> = {
    fecha_nacimiento: (r) => toSortableDate(r.fecha_nacimiento),
    fecha_entrada: (r) => toSortableDate(r.fecha_entrada, r.hora_entrada),
    hora_entrada: (r) => toSortableDate("01/01/2000", r.hora_entrada),
    fecha_salida: (r) => toSortableDate(r.fecha_salida, r.hora_salida),
    hora_salida: (r) => toSortableDate("01/01/2000", r.hora_salida),
}

// TODO: reemplazar por la petición al endpoint cuando esté disponible (usando apiDateFilter, filtro, orden y página)
const useRegistroHuespedesDummy = ({
    currentPage,
    filterValues,
    sortValue,
}: {
    currentPage: number
    filterValues: TableFilter[] | null
    sortValue: SortFilterValue | null
}): RegistroHuespedesType => {
    return useMemo(() => {
        // Filtros de una misma columna se combinan con OR y entre columnas con AND
        const filtersByHeader = (filterValues || []).reduce<Record<string, string[]>>((acc, f) => {
            acc[f.fromHeader] = [...(acc[f.fromHeader] || []), f.filter]
            return acc
        }, {})

        const filtered = registroHuespedesDummy.filter((row) =>
            Object.entries(filtersByHeader).every(([header, values]) =>
                values.includes(String(row[header as keyof TablaRegistroHuesped] ?? ""))
            )
        )

        const getter = sortValue?.sort ? sortGetters[sortValue.fromHeader as HeaderKey] : undefined
        const sorted = getter
            ? [...filtered].sort((a, b) => (sortValue?.sort === "up" ? 1 : -1) * (getter(a) - getter(b)))
            : filtered

        return {
            dashboard: getRegistroHuespedesDashboard(filtered),
            respuesta: {
                pagina_actual: currentPage,
                registros_por_pagina: PAGE_SIZE,
                tabla_registro_huespedes: sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
                total_paginas: Math.ceil(sorted.length / PAGE_SIZE),
                total_registros: sorted.length,
            },
        }
    }, [currentPage, filterValues, sortValue])
}

const RegistroHuespedes = ({ apiDateFilter }: { apiDateFilter: string[] | null }) => {
    const [currentPage, setcurrentPage] = useState(1)
    const [sortValue, setsortValue] = useState<SortFilterValue | null>(null)
    const [filterValues, setfilterValues] = useState<TableFilter[] | null>(null)

    const data = useRegistroHuespedesDummy({ currentPage, filterValues, sortValue })

    const habitaciones = useMemo(() => Array.from(new Set(registroHuespedesDummy.map((r) => r.habitacion))).sort(), [])
    const nacionalidades = useMemo(
        () => Array.from(new Set(registroHuespedesDummy.map((r) => r.nacionalidad).filter(Boolean) as string[])).sort(),
        []
    )

    const headers: (FlexibleTableHeaderColumn & { value: HeaderKey })[] = [
        { value: "folio_ticket", valueToDisplay: "No. Ticket" },
        {
            value: "tipo_huesped",
            valueToDisplay: "Tipo de huésped",
            filterMenu: enumToFilterMenu(Object.values(TipoHuesped)),
        },
        { value: "genero", valueToDisplay: "Género", filterMenu: enumToFilterMenu(Object.values(GeneroHuesped)) },
        { value: "nombre", valueToDisplay: "Nombre" },
        { value: "fecha_nacimiento", valueToDisplay: "Fecha de nacimiento", sort: true },
        { value: "nacionalidad", valueToDisplay: "Nacionalidad", filterMenu: enumToFilterMenu(nacionalidades) },
        {
            value: "tipo_documento",
            valueToDisplay: "Documento",
            filterMenu: enumToFilterMenu(Object.values(TipoDocumentoHuesped)),
        },
        { value: "numero_documento", valueToDisplay: "ID" },
        { value: "curp", valueToDisplay: "CURP" },
        { value: "direccion", valueToDisplay: "Dirección" },
        { value: "fecha_entrada", valueToDisplay: "Fecha de entrada", sort: true },
        { value: "hora_entrada", valueToDisplay: "Hora de entrada", sort: true },
        { value: "fecha_salida", valueToDisplay: "Fecha de salida", sort: true },
        { value: "hora_salida", valueToDisplay: "Hora de salida", sort: true },
        { value: "habitacion", valueToDisplay: "Habitación", filterMenu: enumToFilterMenu(habitaciones) },
    ]

    const rows = data.respuesta.tabla_registro_huespedes

    const { selectedRow, isOpen, openDetail, closeDetail } = useRegistroHuespedesDetail(rows)

    const tableItems: FlexibleTableRow[] = rows.map((r) => ({
        key: r.registro_id,
        // goTo lo recibe el evento goTo de la tabla al dar click en la fila, se usa para abrir el detalle
        goTo: r.registro_id,
        className: "registro-huespedes__row--clickable",
        value: [
            { value: r.folio_ticket, className: "reports-cell" },
            { value: r.tipo_huesped, className: "reports-cell" },
            { value: r.genero || EMPTY_VALUE, className: "reports-cell" },
            { value: r.nombre, className: "reports-cell" },
            { value: r.fecha_nacimiento || EMPTY_VALUE, className: "reports-cell" },
            { value: r.nacionalidad || EMPTY_VALUE, className: "reports-cell" },
            { value: r.tipo_documento, className: "reports-cell" },
            { value: r.numero_documento || EMPTY_VALUE, className: "reports-cell" },
            { value: r.curp || EMPTY_VALUE, className: "reports-cell" },
            {
                value: (
                    <span className="registro-huespedes__direccion" title={r.direccion || undefined}>
                        {r.direccion || EMPTY_VALUE}
                    </span>
                ),
                className: "reports-cell",
            },
            { value: r.fecha_entrada, className: "reports-cell" },
            { value: r.hora_entrada, className: "reports-cell" },
            { value: r.fecha_salida, className: "reports-cell" },
            { value: r.hora_salida, className: "reports-cell" },
            { value: r.habitacion, className: "reports-cell" },
        ],
    }))

    const { dashboard } = data
    const toCardItems = (items: typeof dashboard.por_genero) => items.map((i) => ({ label: i.label, value: i.total }))

    const { setSelectedSection } = reportSelectedHandler()

    useEffect(() => {
        setSelectedSection(ReportsOptions.registroHuespedes)
    }, [])

    useEffect(() => {
        setcurrentPage(1)
    }, [apiDateFilter])

    return (
        <>
            <ReportWrapper height="calc(100dvh - 125px)">
                <CardReports>
                    <CardReport title="Total de registros" value={dashboard.total_registros} />
                    <CardReportDouble
                        title1="Registro por huésped titular"
                        value1={dashboard.total_titulares}
                        title2="Registro por huésped acompañante"
                        value2={dashboard.total_acompanantes}
                    />
                    <CardReportList title="Registros por género" items={toCardItems(dashboard.por_genero)} />
                    <CardReportBars
                        title="Registros por nacionalidad"
                        items={toCardItems(dashboard.por_nacionalidad)}
                    />
                    <CardReportList
                        title="Registros por tipo de documento"
                        items={toCardItems(dashboard.por_tipo_documento)}
                    />
                </CardReports>
                <TablePaginatorWrapper
                    className="registro-huespedes__table__wrapper"
                    currentPage={currentPage}
                    onChange={(p) => setcurrentPage(p)}
                    pages={data.respuesta.total_paginas}
                    style={{
                        justifyContent: rows.length ? "flex-start" : "center",
                        borderRadius: "0 0 20px 0",
                    }}
                >
                    <TableWrappper isThisComponentInsideAPaginatorWrapper={!!rows.length}>
                        <FlexibleTable
                            goTo={openDetail}
                            emptyState={{
                                headerIcon: "PieChartFilled",
                                titile: "Aún no hay reportes disponibles",
                                subTitle:
                                    "Todavía no hay datos para mostrar en esta sección. Una vez que se genere un reporte, lo verás aquí.",
                            }}
                            onSelectedFilters={(v) => {
                                setcurrentPage(1)
                                setfilterValues(v)
                            }}
                            onSort={(v) => {
                                setcurrentPage(1)
                                setsortValue(v)
                            }}
                            containterClassName="registro-huespedes__table"
                            tableItems={{
                                headers,
                                rows: tableItems,
                            }}
                        />
                    </TableWrappper>
                    {/* TODO: conectar la descarga cuando exista el endpoint */}
                    <FloatButon icon="Download" onAdd={() => null} />
                </TablePaginatorWrapper>
            </ReportWrapper>
            <RegistroHuespedesDetail isOpen={isOpen} onClose={closeDetail} data={selectedRow} />
        </>
    )
}

export default RegistroHuespedes
